import {z} from 'zod';
import {congestionWindowsSchema} from './congestion-window';
import {guardApplicationSchema} from './mw-guard';
import {QUARTER_MS} from './dispatch-day';
import {congestionAreaIdSchema, organizationIdSchema, previewIdSchema} from './id';
import {pagingSchema} from './pagination';
import {REQUEST_STATUSES, dispatchDaySchema, requestStatusSchema} from './request';
import {REQUEST_TABS, REQUEST_VIEWS} from './request-view';

export {
  congestionWindowSchema,
  congestionWindowsSchema,
  endKeepingLength,
  windowEndTimesFor,
  WINDOW_START_TIMES,
  type CongestionWindow,
} from './congestion-window';

/**
 * Step one of the wizard. Many areas at once, because a DSO relieving a congestion point
 * usually means several areas on the same day — and one request per (area, day) still
 * holds, so submitting this produces one request per area rather than one spanning them
 * (`docs/dispatch-days.md`).
 */
export const previewRequestSchema = z
  .object({
    congestionAreaIds: z.array(congestionAreaIdSchema).min(1),
    dispatchDay: dispatchDaySchema,
    windows: congestionWindowsSchema,
  })
  .strict();

export type PreviewRequest = z.infer<typeof previewRequestSchema>;

/**
 * The requested flex power for one quarter-hour of the dispatch window.
 *
 * **`quarter` is the instant the quarter-hour starts, in UTC.** It was an index — `quarter_nr`,
 * counted from somewhere nobody had written down — until ElaadNL replaced it on 2026-09-24,
 * and the replacement removes a whole class of question with it: which midnight the count
 * began at (§27), how many quarters a day holds when the clocks move (§31), and whether a
 * dropped first quarter is an off-by-one or an empty hour (§29). An instant is none of those
 * things. A missing quarter is now visibly missing.
 *
 * Turning these quarters into intervals is still ours to do when the event is built — one
 * `PT2H`/`PT4H`/`PT6H` interval per group, cut to the quarters that dispatch and carrying one
 * of these values per quarter of it (ADR 0034, ADR 0037).
 *
 * **Parsed as an instant, kept as one.** `z.coerce.date()` accepts the offset the solver
 * sends and every reader downstream compares instants rather than clock faces, which is what
 * makes a dispatch day that is not a UTC day survivable at all (`answered-questions.md` §33).
 */
/**
 * How far below zero a power may sit and still be zero: one milliwatt, expressed in kW.
 *
 * **The solver's arithmetic leaves noise.** Measured on 2026-09-24 against the deployment:
 * a nine-group solve came back with quarters at `-1.35e-11` kW — thirteen orders of
 * magnitude below a watt, and unmistakably the zero its linear program meant. A bare
 * `min(0)` refused the whole profile over it and the caller was told
 * `PREVIEW_PROFILE_UNREADABLE` about an answer that was perfectly good.
 *
 * Narrow on purpose. A real negative is feed-in, which this profile cannot express and must
 * still be refused — so the tolerance is the width of the noise, not a shrug.
 */
const POWER_NOISE_KW = 1e-6;

export const flexPowerIntervalSchema = z.object({
  /**
   * The instant the quarter starts, and it must be a quarter the grid has: an interval
   * beginning at `08:00:30Z` would travel all the way into a `FLEX` event, because nothing
   * downstream re-derives a timestamp it was handed. Checked here, where the solver's
   * answer first becomes ours.
   */
  quarter: z.coerce.date().refine(value => value.getTime() % QUARTER_MS === 0, {message: 'not_a_quarter'}),
  /**
   * kW. Never negative: a dispatch reduces load, and the sign is not how it says so.
   *
   * `.overwrite()` rather than `.transform()`, because a transform is unrepresentable in
   * JSON Schema and left `flex_power` as `{}` in four places in `docs/openapi.json` — a
   * published contract that no longer said the number is a non-negative one.
   *
   * **The published `minimum: 0` is the bound after clamping, not the bound on the way in**:
   * `.gt(-POWER_NOISE_KW)` accepts the noise, `.overwrite` turns it into the zero it meant,
   * and every value this schema yields is therefore `>= 0`. Nothing we publish an API for
   * sends the noise — it comes from the solver — so the document describes what a client of
   * ours will see.
   */
  flex_power: z
    .number()
    .gt(-POWER_NOISE_KW)
    .overwrite(value => (value < 0 ? 0 : value))
    .nonnegative(),
});

export type FlexPowerInterval = z.infer<typeof flexPowerIntervalSchema>;

export const requestedFlexProfileSchema = z.object({
  /** The resource group's Groupie id, as it was sent in `flex_groups`. */
  flex_group_id: z.string().min(1),
  /**
   * Empty is allowed. "Nothing to dispatch from this group" is an answer a solver may
   * legitimately give, and refusing it here would fail the parse for the whole profile —
   * which the caller would then read as Solvie being unreachable. A profile in which
   * *every* curve is empty is refused by the service, with a code that says so.
   */
  requested_flex: z.array(flexPowerIntervalSchema),
});

export type RequestedFlexProfile = z.infer<typeof requestedFlexProfileSchema>;

/**
 * **One profile per congestion point, not one per group.** Solvie is asked about a
 * congestion point and answers with a single object covering every flex group behind it:
 * the total curve, and the per-group curves that add up to it.
 *
 * Fields are `snake_case` because this is Solvie's own document, stored and handed back
 * exactly as it arrived (ADR 0013). Renaming it on the way in would mean the profile a
 * reviewer reads is not the profile Solvie sent.
 *
 * **Written from the pydantic models ElaadNL sent on 2026-09-04**, which they describe as
 * an early version with changes still to come. Loose on purpose: a field they add arrives
 * intact rather than being dropped on the floor by our parse.
 */
export const solvieProfileSchema = z.looseObject({
  congestion_point_id: z.string().min(1),
  congestion_windows: z
    .array(
      z.looseObject({
        /**
         * Left as the string Solvie sent rather than coerced to a `Date`: the profile is
         * stored verbatim in `jsonb` and in the preview table (ADR 0013), and a `Date`
         * would neither survive that round trip nor be what Solvie said.
         *
         * **An offset is accepted but not required.** The models are pydantic `datetime`
         * fields and nothing says how they serialise; a naive `2026-09-13T09:00:00` is a
         * plausible answer. Requiring an offset would have failed the whole parse, and
         * `previewRequestFor` turns that into `PREVIEW_PROFILE_UNREADABLE` — so a formatting
         * detail would have cost the user their profile.
         *
         * Safe to be lenient here because these two are **echo, not instruction**: what
         * gets dispatched comes from `requested_flex_profiles`, and this pair is only
         * rendered back on the detail screen. `docs/open-questions.md` question 12 asks
         * ElaadNL which it is; a naive value read as local time is right for a Dutch
         * reader and wrong by an hour or two for nobody who currently uses this tool.
         */
        start_time: z.iso.datetime({offset: true, local: true}),
        end_time: z.iso.datetime({offset: true, local: true}),
        percentage_flex: z.number(),
      }),
    )
    .min(1),
  /** The sum over every group. Shown, never published: an event is always per group. */
  total_requested_flex: z.array(flexPowerIntervalSchema),
  requested_flex_profiles: z.array(requestedFlexProfileSchema).min(1),
});

export type SolvieProfile = z.infer<typeof solvieProfileSchema>;

/** One calculated profile, for one area, held server-side under its own id. */
export const areaPreviewSchema = z.object({
  congestionAreaId: congestionAreaIdSchema,
  congestionAreaName: z.string(),
  previewId: previewIdSchema,
  /**
   * The profile as it will be dispatched — Solvie's answer with any MW guard already
   * applied (ADR 0036). What the wizard draws and what submit freezes are the same
   * document, so nobody approves a curve that is later trimmed.
   */
  profile: solvieProfileSchema,
  /**
   * Solvie's answer before the guard, present only when a guard cut something. The wizard
   * draws it behind the profile so the reader can see what was taken off.
   */
  solverProfile: solvieProfileSchema.nullable(),
  /** Which ceilings were in force and what they did. Null when the area is under none. */
  guard: guardApplicationSchema.nullable(),
  /**
   * What this area's groups had on offer when the profile was calculated, in kW — the
   * denominator the dispatch is read against. Liander asked for it on the profile step
   * (feedback, 2 October): a peak means little without what was available to take.
   *
   * Frozen with the preview rather than read again, for the same reason the profile is:
   * the number a requester saw is the number that was used.
   */
  offeredKw: z.number().nonnegative(),
});

export type AreaPreview = z.infer<typeof areaPreviewSchema>;

export const previewResponseSchema = z.object({
  previews: z.array(areaPreviewSchema).min(1),
  /** How long the caller has to submit before the previews expire. */
  ttlSeconds: z.number().int().positive(),
});

export type PreviewResponse = z.infer<typeof previewResponseSchema>;

/**
 * Submit carries preview ids and nothing else — no profile, no windows, not even the
 * congestion areas. All of it comes from the server-side previews, and `.strict()` is
 * what turns a smuggled `profile` into a refusal instead of a silently ignored field.
 *
 * All of them or none: one press of one button should not leave three of five areas
 * dispatched, with the user guessing which.
 */
export const submitRequestSchema = z
  .object({
    previewIds: z.array(previewIdSchema).min(1),
  })
  .strict();

export type SubmitRequest = z.infer<typeof submitRequestSchema>;

export const listRequestsQuerySchema = pagingSchema
  .extend({
    /**
     * Which of the screen's chips. A view rather than a status, because several cover more
     * than one (`domain/request-view.ts`).
     */
    view: z.enum(REQUEST_VIEWS).optional(),
    /**
     * Which days: the dispatch days still ahead, or the ones already over. Separate from
     * `view` because the two are independent — the past tab filters by chip exactly as the
     * active one does — and defaulted to `ACTIVE`, which is the screen a reader opens on.
     */
    tab: z.enum(REQUEST_TABS).optional(),
    /**
     * One status out of the chosen view — the second chip row, which narrows a group to a
     * single state. Independent of `view`: a status outside the selected view simply
     * intersects to nothing rather than being an error, because the two arrive from
     * separate controls and the server does not police the reader's combination.
     */
    status: requestStatusSchema.optional(),
    from: dispatchDaySchema.optional(),
    to: dispatchDaySchema.optional(),
  })
  .strict();

export type ListRequestsQuery = z.infer<typeof listRequestsQuerySchema>;

/**
 * The number on each filter chip. One object rather than a count per request, because the
 * chips are drawn together and a screen asking seven questions to render one row of
 * buttons is seven round trips.
 */
/** The chips count whichever tab is open, so the tab travels with the question. */
export const requestCountsQuerySchema = z.object({tab: z.enum(REQUEST_TABS).optional()}).strict();

export type RequestCountsQuery = z.infer<typeof requestCountsQuerySchema>;

const countsOf = <Key extends string>(keys: readonly Key[]) =>
  z.object(Object.fromEntries(keys.map(key => [key, z.number().int().nonnegative()])) as Record<Key, z.ZodNumber>);

/**
 * Both chip rows in one answer. `views` sums `statuses` — the groups partition the twelve
 * states — but it is sent rather than summed in the browser, so the definition of a group
 * stays in `domain/request-view.ts` and the screen keeps no arithmetic of its own.
 */
export const requestCountsSchema = z.object({
  views: countsOf(REQUEST_VIEWS),
  statuses: countsOf(REQUEST_STATUSES),
});

export type RequestCounts = z.infer<typeof requestCountsSchema>;

/**
 * Congestion areas the caller may dispatch against — the first thing the wizard asks for,
 * and the only list of them the API offers. Rows are seeded or entered elsewhere; nothing
 * in this API creates one (`docs/open-questions.md`).
 */
export const listCongestionAreasQuerySchema = pagingSchema
  .extend({
    /**
     * Which tenant's areas. Only a platform admin may name one, and they must: areas
     * belong to an organization, so "all of them" is a question with no useful answer.
     */
    organizationId: organizationIdSchema.optional(),
    /**
     * Only the areas a request may be made against. The wizard asks for `true`; the
     * settings screen asks for neither, because an administrator turning one back on has
     * to be able to see it.
     *
     * A string on the wire because a query string has no booleans, and narrow rather than
     * coerced: `z.coerce.boolean()` reads `"false"` as `true`, which would quietly offer
     * every deactivated area.
     */
    active: z
      .enum(['true', 'false'])
      .transform(value => value === 'true')
      .optional(),
  })
  .strict();

export type ListCongestionAreasQuery = z.infer<typeof listCongestionAreasQuerySchema>;

export const REQUEST_DECISIONS = ['approve', 'disapprove'] as const;

export type RequestDecision = (typeof REQUEST_DECISIONS)[number];

/** A reason may be attached to either decision and is never required — nothing reads it. */
export const decisionRequestSchema = z
  .object({
    decision: z.enum(REQUEST_DECISIONS),
    reason: z.string().max(500).optional(),
  })
  .strict();

export type DecisionRequest = z.infer<typeof decisionRequestSchema>;

export const cancelRequestSchema = z
  .object({
    reason: z.string().max(500).optional(),
  })
  .strict();

export type CancelRequest = z.infer<typeof cancelRequestSchema>;
